// Importar extractos — parsea un CSV según el formato configurado, previsualiza,
// y registra los movimientos con deduplicación (RPC cb_importar_extracto).
// Control Bancario → Movimientos → Importar extractos.
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/lib/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, CuentaMini, FormatoExtracto, cb, cbRpc, fieldCls, money } from './shared';

interface Lote { id: string; archivo_nombre: string | null; fecha_desde: string | null; fecha_hasta: string | null; total_filas: number; importadas: number; duplicadas: number; estado: string; created_at: string; cuenta?: { alias: string | null; numero: string }; }
interface FilaNorm { fecha: string; concepto: string; referencia: string; tipo: 'credito' | 'debito'; monto: number; _err?: string; }

// --- utilidades de parseo -----------------------------------------------------
const limpiaMonto = (s: string): number => {
  if (!s) return 0;
  let x = s.replace(/[^\d.,-]/g, '').trim();
  if (x.includes(',') && x.includes('.')) x = x.lastIndexOf(',') > x.lastIndexOf('.') ? x.replace(/\./g, '').replace(',', '.') : x.replace(/,/g, '');
  else if (x.includes(',')) x = x.replace(',', '.');
  const n = parseFloat(x);
  return isNaN(n) ? 0 : n;
};
const normFecha = (raw: string, patron: string): string | null => {
  const s = (raw || '').trim();
  if (!s) return null;
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const p = patron.toUpperCase();
  const parts = s.split(/[/\-.]/);
  if (parts.length < 3) return null;
  let d: string, m: string, y: string;
  if (p.startsWith('MM')) { [m, d, y] = parts; } else if (p.startsWith('YYYY')) { [y, m, d] = parts; } else { [d, m, y] = parts; }
  if (y.length === 2) y = '20' + y;
  if (!d || !m || !y) return null;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};
// resuelve un mapeo (nombre de columna o índice) a un índice de columna
const idxDe = (mapeo: string | null, headers: string[]): number => {
  if (!mapeo) return -1;
  const m = mapeo.trim();
  const byName = headers.findIndex((h) => h.trim().toLowerCase() === m.toLowerCase());
  if (byName >= 0) return byName;
  const n = parseInt(m, 10);
  return isNaN(n) ? -1 : n;
};

function parseCSV(texto: string, fmt: FormatoExtracto): FilaNorm[] {
  const delim = fmt.delimitador || ',';
  const lineas = texto.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lineas.length === 0) return [];
  const headers = fmt.tiene_encabezado ? lineas[0].split(delim) : [];
  const datos = fmt.tiene_encabezado ? lineas.slice(1) : lineas;
  const iF = idxDe(fmt.col_fecha, headers), iD = idxDe(fmt.col_descripcion, headers), iR = idxDe(fmt.col_referencia, headers);
  const iDeb = idxDe(fmt.col_debito, headers), iCre = idxDe(fmt.col_credito, headers);
  return datos.map((linea) => {
    const c = linea.split(delim);
    const fecha = normFecha(iF >= 0 ? c[iF] : '', fmt.formato_fecha);
    const deb = iDeb >= 0 ? limpiaMonto(c[iDeb] ?? '') : 0;
    const cre = iCre >= 0 ? limpiaMonto(c[iCre] ?? '') : 0;
    let tipo: 'credito' | 'debito' = 'debito'; let monto = 0;
    if (cre > 0) { tipo = 'credito'; monto = cre; } else if (deb > 0) { tipo = 'debito'; monto = deb; }
    const fila: FilaNorm = {
      fecha: fecha ?? '', concepto: (iD >= 0 ? c[iD] : '')?.trim() ?? '',
      referencia: (iR >= 0 ? c[iR] : '')?.trim() ?? '', tipo, monto,
    };
    if (!fecha) fila._err = i18n.t('bancos:extractos.err_fecha');
    else if (monto <= 0) fila._err = i18n.t('bancos:extractos.err_monto');
    return fila;
  });
}

export function ImportarExtractos() {
  const { t } = useTranslation('bancos');
  const qc = useQueryClient();
  const { data: cuentas = [] } = useQuery<CuentaMini[]>({
    queryKey: ['cb-cuentas-mini'],
    queryFn: () => cb.list<CuentaMini>('cuentas', '?select=id,alias,numero,moneda,banco:banco_id(nombre)&deleted_at=is.null&es_activo=is.true&order=alias,numero'),
  });
  const { data: formatos = [] } = useQuery<FormatoExtracto[]>({
    queryKey: ['cb-formatos'],
    queryFn: () => cb.list<FormatoExtracto>('formatos_extracto', '?select=*&deleted_at=is.null&activo=is.true&order=nombre'),
  });
  const { data: lotes = [], isFetching } = useQuery<Lote[]>({
    queryKey: ['cb-importaciones'],
    queryFn: () => cb.list<Lote>('movimientos_importacion', '?select=*,cuenta:cuenta_id(alias,numero)&order=created_at.desc&limit=50'),
  });

  const [cuentaId, setCuentaId] = useState('');
  const [formatoId, setFormatoId] = useState('');
  const [texto, setTexto] = useState('');
  const [archivo, setArchivo] = useState('');
  const [filas, setFilas] = useState<FilaNorm[]>([]);

  const fmt = formatos.find((f) => f.id === formatoId);
  const validas = useMemo(() => filas.filter((f) => !f._err), [filas]);
  const conError = filas.length - validas.length;
  const moneda = cuentas.find((c) => c.id === cuentaId)?.moneda ?? 'USD';

  const onFile = (f: File | null) => {
    if (!f) return;
    setArchivo(f.name);
    const r = new FileReader();
    r.onload = () => setTexto(String(r.result ?? ''));
    r.readAsText(f);
  };

  const previsualizar = () => {
    if (!fmt) { toast.error(t('extractos.sel_formato')); return; }
    if (!texto.trim()) { toast.error(t('extractos.sel_contenido')); return; }
    const res = parseCSV(texto, fmt);
    setFilas(res);
    if (res.length === 0) toast.error(t('extractos.sin_filas'));
    else toast.success(t('extractos.filas_ok', { n: res.length }));
  };

  const importar = useMutation({
    mutationFn: () => cbRpc<{ total: number; importadas: number; duplicadas: number; conciliados: number }>('cb_importar_extracto', {
      _cuenta_id: cuentaId, _formato_id: formatoId || null, _archivo_nombre: archivo || 'extracto',
      _filas: validas.map((f) => ({ fecha: f.fecha, concepto: f.concepto, referencia: f.referencia, tipo: f.tipo, monto: f.monto })),
    }),
    onSuccess: (r) => {
      toast.success(`${t('extractos.importado_ok', { importadas: r.importadas, duplicadas: r.duplicadas })}${r.conciliados > 0 ? t('extractos.importado_auto', { n: r.conciliados }) : ''}`);
      setFilas([]); setTexto(''); setArchivo('');
      void qc.invalidateQueries({ queryKey: ['cb-importaciones'] });
      void qc.invalidateQueries({ queryKey: ['cb-libro'] });
      void qc.invalidateQueries({ queryKey: ['cb-posicion'] });
      void qc.invalidateQueries({ queryKey: ['cb-conciliacion'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><Upload className="w-5 h-5" /> {t('extractos.titulo')}</h1>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('extractos.paso1')}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Campo label={t('extractos.f_cuenta')}>
              <select className={fieldCls} value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}>
                <option value="">{t('extractos.selecciona')}</option>
                {cuentas.map((c) => <option key={c.id} value={c.id}>{c.banco?.nombre} · {c.alias ?? c.numero} ({c.moneda})</option>)}
              </select>
            </Campo>
            <Campo label={t('extractos.f_formato')}>
              <select className={fieldCls} value={formatoId} onChange={(e) => setFormatoId(e.target.value)}>
                <option value="">{t('extractos.selecciona')}</option>
                {formatos.map((f) => <option key={f.id} value={f.id}>{f.nombre} ({f.tipo.toUpperCase()})</option>)}
              </select>
            </Campo>
          </div>
          {formatos.length === 0 && <p className="text-sm text-muted-foreground">{t('extractos.sin_formatos')}</p>}
          {fmt && fmt.tipo !== 'csv' && <p className="text-sm text-pendiente">{t('extractos.aviso_no_csv', { tipo: fmt.tipo.toUpperCase() })}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 cursor-pointer rounded-md border px-3 py-2 text-sm hover:bg-muted">
              <FileUp className="w-4 h-4" /> {archivo || t('extractos.cargar_archivo')}
              <input type="file" accept=".csv,.txt" className="hidden" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
            </label>
            <span className="text-xs text-muted-foreground">{t('extractos.o_pega')}</span>
          </div>
          <textarea className={`${fieldCls} h-32 font-mono text-xs`} placeholder="fecha,descripcion,referencia,debito,credito&#10;2026-07-01,Deposito,REF001,,500.00" value={texto} onChange={(e) => setTexto(e.target.value)} />
          <div className="flex justify-end">
            <Button variant="outline" onClick={previsualizar} disabled={!cuentaId || !formatoId}>{t('extractos.btn_previsualizar')}</Button>
          </div>
        </CardContent>
      </Card>

      {filas.length > 0 && (
        <Card>
          <CardHeader className="pb-2 flex-row items-center justify-between">
            <CardTitle className="text-base">{t('extractos.paso2', { n: validas.length })}{conError > 0 && <span className="text-pendiente">{t('extractos.con_error', { n: conError })}</span>}</CardTitle>
            <Button isLoading={importar.isPending} disabled={validas.length === 0} onClick={() => importar.mutate()}>
              <Upload className="w-4 h-4 mr-1" /> {t('extractos.btn_importar', { n: validas.length })}
            </Button>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">{t('extractos.col_fecha')}</th><th className="py-2 pr-3">{t('extractos.col_concepto')}</th><th className="py-2 pr-3">{t('extractos.col_ref')}</th><th className="py-2 pr-3">{t('extractos.col_tipo')}</th><th className="py-2 pr-3 text-right">{t('extractos.col_monto')}</th><th className="py-2 pr-3">{t('extractos.col_estado')}</th>
              </tr></thead>
              <tbody>
                {filas.slice(0, 200).map((f, i) => (
                  <tr key={i} className={`border-b last:border-0 ${f._err ? 'bg-pendiente-soft' : ''}`}>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{f.fecha || '—'}</td>
                    <td className="py-1.5 pr-3">{f.concepto || '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{f.referencia || '—'}</td>
                    <td className="py-1.5 pr-3 capitalize">{t(`extractos.tipo.${f.tipo}`)}</td>
                    <td className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(f.monto, moneda)}</td>
                    <td className="py-1.5 pr-3 text-xs">{f._err ? <span className="text-pendiente">{f._err}</span> : <span className="text-estable">OK</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filas.length > 200 && <p className="pt-2 text-xs text-muted-foreground">{t('extractos.mostrando', { n: filas.length })}</p>}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('extractos.recientes', { n: lotes.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('extractos.col_fecha')}</th><th className="py-2 pr-3">{t('extractos.col_cuenta')}</th><th className="py-2 pr-3">{t('extractos.col_archivo')}</th><th className="py-2 pr-3">{t('extractos.col_periodo')}</th><th className="py-2 pr-3 text-right">{t('extractos.col_total')}</th><th className="py-2 pr-3 text-right">{t('extractos.col_importadas')}</th><th className="py-2 pr-3 text-right">{t('extractos.col_duplicadas')}</th>
            </tr></thead>
            <tbody>
              {lotes.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">{t('extractos.vacio')}</td></tr>}
              {lotes.map((l) => (
                <tr key={l.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{l.created_at?.slice(0, 10)}</td>
                  <td className="py-2 pr-3">{l.cuenta?.alias ?? l.cuenta?.numero ?? '—'}</td>
                  <td className="py-2 pr-3">{l.archivo_nombre ?? '—'}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-xs">{l.fecha_desde ?? '—'} → {l.fecha_hasta ?? '—'}</td>
                  <td className="py-2 pr-3 text-right font-mono">{l.total_filas}</td>
                  <td className="py-2 pr-3 text-right font-mono text-estable">{l.importadas}</td>
                  <td className="py-2 pr-3 text-right font-mono text-muted-foreground">{l.duplicadas}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
